import { IContact, IRoleWrapper, IUserProperty, } from 'src/services/api/ServiceRequestDashboard/apiContracts';

export type RoleFilter = string | 'all';
export type ProjectFilter = string | 'all';

export interface StagedEntry {
  contact: IContact;
  newValue: boolean;
}

const DEFAULT_ALLOW_EMAIL_CHANGE: boolean = true;

export const getOriginalAllowEmailChange = (contact: IContact): boolean => {
  if (typeof contact.allowEmailChange === 'boolean') {
    return contact.allowEmailChange;
  }

  return DEFAULT_ALLOW_EMAIL_CHANGE;
};

export const buildStagedEntry = (
  contact: IContact,
  newValue: boolean
): StagedEntry => {
  return { contact, newValue };
};

export const indexContactsById = (
  contacts: IContact[]
): Map<string, IContact> => {
  const byId: Map<string, IContact> = new Map();

  for (const contact of contacts) {
    byId.set(contact.id, contact);
  }

  return byId;
};

export const contactMatchesSearchQuery = (
  contact: IContact,
  query: string
): boolean => {
  const trimmed: string = query.trim();

  if (trimmed.length === 0) {
    return true;
  }

  const needle: string = trimmed.toLowerCase();
  const parts: Array<string | null | undefined> = [
    contact.firstname,
    contact.lastname,
    contact.email,
    contact.companyName,
  ];
  const presentParts: string[] = parts.filter(
    (part: string | null | undefined): part is string => Boolean(part)
  );
  const haystack: string = presentParts.join(' ').toLowerCase();

  return haystack.includes(needle);
};

export const contactMatchesRoleFilter = (
  contact: IContact,
  roleFilter: RoleFilter
): boolean => {
  if (roleFilter === 'all') {
    return true;
  }

  return contact.roles.some(
    (wrapper: IRoleWrapper) => wrapper.role?.name === roleFilter
  );
};

export const contactMatchesProjectFilter = (
  contact: IContact,
  projectFilter: ProjectFilter
): boolean => {
  if (projectFilter === 'all') {
    return true;
  }

  const properties: IUserProperty[] = contact.properties ?? [];

  return properties.some(
    (property: IUserProperty) => property.propertyId === projectFilter
  );
};

export const filterContacts = (
  contacts: IContact[],
  roleFilter: RoleFilter,
  projectFilter: ProjectFilter,
  searchQuery: string
): IContact[] => {
  const matches: IContact[] = [];

  for (const contact of contacts) {
    const roleOk: boolean = contactMatchesRoleFilter(contact, roleFilter);

    if (!roleOk) {
      continue;
    }

    const projectOk: boolean = contactMatchesProjectFilter(
      contact,
      projectFilter
    );

    if (!projectOk) {
      continue;
    }

    const searchOk: boolean = contactMatchesSearchQuery(contact, searchQuery);

    if (!searchOk) {
      continue;
    }

    matches.push(contact);
  }

  return matches;
};

export const toggleIdInSelection = (
  previous: Set<string>,
  id: string
): Set<string> => {
  const next: Set<string> = new Set(previous);

  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }

  return next;
};

export const applySelectionToIds = (
  previous: Set<string>,
  ids: string[],
  selected: boolean
): Set<string> => {
  const next: Set<string> = new Set(previous);

  for (const id of ids) {
    if (selected) {
      next.add(id);
    } else {
      next.delete(id);
    }
  }

  return next;
};

export const updateStagedEntryValue = (
  previous: Map<string, StagedEntry>,
  id: string,
  allow: boolean
): Map<string, StagedEntry> => {
  const existing: StagedEntry | undefined = previous.get(id);

  if (!existing) {
    return previous;
  }

  const next: Map<string, StagedEntry> = new Map(previous);
  const updated: StagedEntry = buildStagedEntry(existing.contact, allow);

  next.set(id, updated);

  return next;
};

export const removeStagedEntry = (
  previous: Map<string, StagedEntry>,
  id: string
): Map<string, StagedEntry> => {
  if (!previous.has(id)) {
    return previous;
  }

  const next: Map<string, StagedEntry> = new Map(previous);
  next.delete(id);

  return next;
};

export const bulkUpdateStagedValues = (
  previous: Map<string, StagedEntry>,
  allow: boolean
): Map<string, StagedEntry> => {
  const next: Map<string, StagedEntry> = new Map();

  for (const [id, entry] of previous.entries()) {
    const updated: StagedEntry = buildStagedEntry(entry.contact, allow);

    next.set(id, updated);
  }

  return next;
};

export const mergeSelectedIntoStaging = (
  previous: Map<string, StagedEntry>,
  selectedIds: Set<string>,
  contactsById: Map<string, IContact>
): Map<string, StagedEntry> => {
  const next: Map<string, StagedEntry> = new Map(previous);

  for (const userId of selectedIds) {
    if (next.has(userId)) {
      continue;
    }

    const contact: IContact | undefined = contactsById.get(userId);

    if (!contact) {
      continue;
    }

    /* Match the bulk toggle's default Disallow position so step 2's
     * buckets render correctly on first mount. Flip if the toggle
     * default ever changes. */
    const initialNewValue: boolean = false;
    const entry: StagedEntry = buildStagedEntry(contact, initialNewValue);

    next.set(userId, entry);
  }

  return next;
};

export const filterActualChanges = (entries: StagedEntry[]): StagedEntry[] => {
  return entries.filter(
    (entry: StagedEntry) =>
      getOriginalAllowEmailChange(entry.contact) !== entry.newValue
  );
};

export const extractContactIdsByNewValue = (
  entries: StagedEntry[],
  newValue: boolean
): string[] => {
  const matching: StagedEntry[] = entries.filter(
    (entry: StagedEntry) => entry.newValue === newValue
  );

  const ids: string[] = matching.map((entry: StagedEntry) => entry.contact.id);

  return ids;
};

export const filterEntriesByNewValue = (
  entries: StagedEntry[],
  newValue: boolean
): StagedEntry[] => {
  return entries.filter((entry: StagedEntry) => entry.newValue === newValue);
};
